package com.example.hukakmp.identification

import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.browser.window

actual val fishIdentificationAvailable: Boolean = true

private const val MODEL_NAME = "gemini-3.8-flash"

private val json = Json {
    ignoreUnknownKeys = true
    isLenient = true
}

@Serializable
private data class AiIdentificationResponse(
    val isFishingImage: Boolean = false,
    val species: List<AiSpecies> = emptyList(),
    val summary: String = "",
)

@Serializable
private data class AiSpecies(
    val commonName: String = "",
    val scientificName: String? = null,
    val confidence: Int? = null,
)

actual suspend fun identifyFishPhoto(
    bytes: ByteArray,
    contentType: String,
): FishIdentificationResult {
    if (bytes.isEmpty()) {
        return FishIdentificationResult.Error("La imagen está vacía.")
    }

    return try {
        val backendOptions = js("{}")
        backendOptions.backend = GoogleAIBackend()

        val ai = getAI(firebaseAiApp(), backendOptions)
        val modelParams = js("{}")
        modelParams.model = MODEL_NAME
        val model = getGenerativeModel(ai, modelParams)

        val imagePart = js("{}")
        val inlineData = js("{}")
        inlineData.data = bytes.toBase64()
        inlineData.mimeType = contentType.ifBlank { "image/jpeg" }
        imagePart.inlineData = inlineData

        val result = model.generateContent(
            arrayOf<dynamic>(
                IDENTIFICATION_PROMPT,
                imagePart,
            ),
        ).await()

        val raw = result.response.text().trim()
        val cleaned = raw
            .removePrefix("```json")
            .removePrefix("```JSON")
            .removePrefix("```")
            .removeSuffix("```")
            .trim()

        val parsed = json.decodeFromString<AiIdentificationResponse>(cleaned)

        FishIdentificationResult.Success(
            FishIdentification(
                isFishingImage = parsed.isFishingImage,
                species = parsed.species
                    .filter { it.commonName.isNotBlank() }
                    .map {
                        IdentifiedFish(
                            commonName = it.commonName.trim(),
                            scientificName = it.scientificName?.trim()?.takeIf(String::isNotBlank),
                            confidence = it.confidence?.coerceIn(0, 100),
                        )
                    },
                summary = parsed.summary.trim().ifBlank {
                    if (parsed.isFishingImage) {
                        "La imagen parece estar relacionada con pesca, pero no pude determinar la especie con suficiente precisión."
                    } else {
                        "La imagen no parece mostrar una captura o una escena útil para identificar un pez."
                    }
                },
            ),
        )
    } catch (e: Throwable) {
        FishIdentificationResult.Error(
            when {
                e.message?.contains("permission", ignoreCase = true) == true ->
                    "Firebase AI Logic rechazó la solicitud. Revisá la configuración y App Check del proyecto."
                e.message?.contains("quota", ignoreCase = true) == true ->
                    "La cuota de identificación por IA no está disponible en este momento."
                e.message?.contains("404", ignoreCase = true) == true ->
                    "El modelo de identificación no está disponible en Firebase AI Logic."
                else -> "No se pudo analizar la fotografía. Intentá nuevamente."
            },
        )
    }
}

private fun ByteArray.toBase64(): String {
    val binary = buildString(size) {
        this@toBase64.forEach { byte ->
            append((byte.toInt() and 0xFF).toChar())
        }
    }
    return window.btoa(binary)
}

private val IDENTIFICATION_PROMPT = """
Analizá esta fotografía para Huka, una aplicación argentina de pesca recreativa.

Tu tarea es decidir primero si la imagen sirve para identificar una captura o una especie de pez.
Si la foto NO muestra un pez, una captura o una escena de pesca útil para identificar una especie:
- isFishingImage debe ser false
- species debe ser []
- explicá brevemente el problema en summary

Si sí muestra uno o más peces:
- isFishingImage debe ser true
- identificá como máximo 3 especies visibles
- usá el nombre común usado en Argentina cuando sea posible
- agregá nombre científico solo si tenés confianza razonable
- confidence debe ser un entero de 0 a 100
- no inventes una especie si la imagen no permite distinguirla

Respondé SOLAMENTE JSON válido, sin markdown ni texto fuera del JSON:
{
  "isFishingImage": true,
  "species": [
    {
      "commonName": "nombre común",
      "scientificName": "Nombre científico",
      "confidence": 85
    }
  ],
  "summary": "explicación breve y útil"
}
""".trimIndent()
